import { range } from './js';
import { getDocumentLang } from './lang';
import { upperFirst } from './string';

/**
 * Check if two dates are the same day, ignoring the time.
 *
 * @param {Date} dateA - The first date.
 * @param {Date} dateB - The second date.
 * @returns {boolean}
 */
export function isSameDay(dateA, dateB) {
  if (!dateA || !dateB) {
    return false;
  }

  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

/**
 * Check if two dates are the same month, ignoring the day and time.
 *
 * @param {Date} dateA - The first date.
 * @param {Date} dateB - The second date.
 * @returns {boolean}
 */
export function isSameMonth(dateA, dateB) {
  if (!dateA || !dateB) {
    return false;
  }

  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth()
  );
}

/**
 * Format a date as standard ISO YYYY-MM-DD.
 *
 * @param {Date} date - The date to format.
 * @returns {string}
 */
export function formatDateYMD(date) {
  return [
    String(date.getFullYear()),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * Format a date using `toLocaleString` with the document's language.
 *
 * @param {Date} date - The date to format.
 * @param {object} options - The options for `toLocaleString`.
 * @param {string} [locale] - Force another locale.
 * @returns {string}
 */
export function localeFormat(date, options, locale = null) {
  // Fall back to English if no lang is set.
  const lang = locale || getDocumentLang('en');
  // Use English as a fallback in case the specified locale isn't supported.
  return date.toLocaleString([lang, 'en'], options);
}

/**
 * Get a list of localized weekday names.
 *
 * @param {string} format - Format to use, 'long', 'short' or 'narrow'.
 * @param {string} [locale] - Force another locale.
 * @returns {Array.<string>}
 */
export function getWeekdayNames(format = 'long', locale = null) {
  return range(0, 6).map((dayNum) =>
    // June 6th 2016 was a Monday. Include English as a fallback in case the
    // current document's language isn't supported.
    upperFirst(
      localeFormat(new Date(2016, 5, 6 + dayNum), { weekday: format }, locale),
    ),
  );
}

/**
 * Get a list of localized weekday names.
 *
 * @param {string} format - Format to use, 'long', 'short' or 'narrow'.
 * @param {string} [locale] - Force another locale.
 * @returns {Array.<string>}
 */
export function getMonthNames(format = 'long', locale = null) {
  return range(0, 11).map((monthNum) =>
    upperFirst(
      localeFormat(new Date(2016, 0 + monthNum, 1), { month: format }, locale),
    ),
  );
}

/**
 * Get the first day of the week for the specified date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getStartOfWeek(date) {
  const firstDay = new Date(date);
  const day = firstDay.getDay();
  // First day of the week (0) in JS is Sunday, we want Monday.
  const diff = day >= 1 ? day - 1 : 6 - day;

  firstDay.setDate(firstDay.getDate() - diff);
  firstDay.setHours(0, 0, 0, 0);

  return firstDay;
}

/**
 * Get the last day of the week for the specified date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getEndOfWeek(date) {
  const lastDay = new Date(date);
  // const day = lastDay.getDay();
  // Last day of the week (6) in JS is Saturday, we want Sunday.
  const diff = lastDay.getDay() !== 0 ? 6 - date.getDay() + 1 : 0;

  lastDay.setDate(lastDay.getDate() + diff);
  lastDay.setHours(23, 59, 59, 999);

  return lastDay;
}

/**
 * Get the first day of the month for the specified date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getStartOfMonth(date) {
  const newDate = new Date(date);
  newDate.setDate(1);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}

/**
 * Get the last day of the month for the specified date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getEndOfMonth(date) {
  const newDate = new Date(date);
  // The 'zeroeth' day of a month is the last day of the previous month.
  newDate.setFullYear(newDate.getFullYear(), newDate.getMonth() + 1, 0);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
}

/**
 * Add a number of days to the specified date.
 *
 * @param {Date} date - The date to change.
 * @param {number} days - Number of days to add.
 * @returns {Date}
 */
export function addDays(date, days) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

/**
 * Subtract a number of days from the specified date.
 *
 * @param {Date} date - The date to change.
 * @param {number} days - Number of days to subtract.
 * @returns {Date}
 */
export function subtractDays(date, days) {
  return addDays(date, -days);
}

/**
 * Add a number of months to the specified date.
 *
 * @param {Date} date - The date to change.
 * @param {number} months - Number of months to add.
 * @returns {Date}
 */
export function addMonths(date, months) {
  // Logic and comments from date-fns

  const newDate = new Date(date);
  const dayOfMonth = newDate.getDate();

  // The JS Date object supports date math by accepting out-of-bounds values for
  // month, day, etc. For example, new Date(2020, 0, 0) returns 31 Dec 2019 and
  // new Date(2020, 13, 1) returns 1 Feb 2021.  This is *almost* the behavior
  // we want except that dates will wrap around the end of a month, meaning
  // that new Date(2020, 13, 31) will return 3 Mar 2021 not 28 Feb 2021 as
  // desired. So we'll default to the end of the desired month by adding 1 to
  // the desired month and using a date of 0 to back up one day to the end of
  // the desired month.
  const endOfDesiredMonth = new Date(newDate.getTime());
  endOfDesiredMonth.setMonth(newDate.getMonth() + months + 1, 0);
  const daysInMonth = endOfDesiredMonth.getDate();

  // If we're already at the end of the month, then this is the correct date
  // and we're done.
  if (dayOfMonth >= daysInMonth) {
    return endOfDesiredMonth;
  }

  // Otherwise, we now know that setting the original day-of-month value won't
  // cause an overflow, so set the desired day-of-month. Note that we can't
  // just set the date of `endOfDesiredMonth` because that object may have had
  // its time changed in the unusual case where where a DST transition was on
  // the last day of the month and its local time was in the hour skipped or
  // repeated next to a DST transition.  So we use `date` instead which is
  // guaranteed to still have the original time.
  newDate.setFullYear(
    endOfDesiredMonth.getFullYear(),
    endOfDesiredMonth.getMonth(),
    dayOfMonth,
  );
  return newDate;
}

/**
 * Subtract a number of months from the specified date.
 *
 * @param {Date} date - The date to change.
 * @param {number} months - Number of months to subtract.
 * @returns {Date}
 */
export function subtractMonths(date, months) {
  return addMonths(date, -months);
}

/**
 * Add a number of years from the specified date.
 *
 * @param {Date} date - The date to change.
 * @param {number} years - Number of years to add.
 * @returns {Date}
 */
export function addYears(date, years) {
  return addMonths(date, years * 12);
}

/**
 * Subtract a number of years from the specified date.
 *
 * @param {Date} date - The date to change.
 * @param {number} years - Number of years to subtract.
 * @returns {Date}
 */
export function subtractYears(date, years) {
  return addYears(date, -years);
}
